import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment';
import { ErrorHandler, APP_INITIALIZER, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { registerLocaleData } from '@angular/common';
import localeCs from '@angular/common/locales/cs';
import moment from 'moment';
import 'moment/locale/cs';

registerLocaleData(localeCs);
import { provideRouter, Router, withPreloading, NoPreloading, withInMemoryScrolling } from '@angular/router';
import { BASE_PATH as API_BASE_PATH } from './app/api/variables';
import { BASE_PATH as API_USERS_BASE_PATH } from './app/api-users/variables';
import { BASE_PATH as INTERNAL_API_BASE_PATH } from './app/api-internal/variables';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptorsFromDi, HttpClient } from '@angular/common/http';
import { appRoutes } from './app/app.routing';
import { FuseModule, FuseConfigModule, FuseMockApiModule, FuseLoadingBarModule } from 'fuseui';
import { appConfig } from './app/core/config/app.config';
import { mockApiServices } from './app/mock-api';
import { CoreModule } from './app/core/core.module';
import { LayoutModule } from 'sting';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { NgxTranslateCacheBusterLoader } from './app/core/transloco/ngx-translate-cache-buster.loader';
import { AppComponent } from './app/app.component';
import { MatSelectModule } from '@angular/material/select';
import { provideBrnCalendarI18n } from '@spartan-ng/brain/calendar';
import { provideNativeDateAdapter } from '@spartan-ng/brain/date-time';
import { CS_CALENDAR_I18N } from './app/core/config/calendar-i18n';

// Load Clarity only in production
function loadClarity() {
    if (environment.production) {
        (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "sfsrk0ho1o");

        // Automatický souhlas podle V2 API
        (window as any).clarity('consentv2', {
            ad_storage: "granted",
            analytics_storage: "granted"
        });
    }
}

export function keepMaterialSelect() {
    return MatSelectModule;
}
keepMaterialSelect();

Sentry.init({
    dsn: "https://78649b2379e19ebd44c4071354f350b6@o4512254476877824.ingest.de.sentry.io/4512367784362064",
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    environment: environment.production ? 'production' : 'development',
    enabled: environment.production,
    tracePropagationTargets: [/^https:\/\/sting\.service\.rksting\.cz/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

function czechPaginatorIntl(): MatPaginatorIntl {
    const intl = new MatPaginatorIntl();
    intl.itemsPerPageLabel = 'Položek na stránku:';
    intl.nextPageLabel = 'Další stránka';
    intl.previousPageLabel = 'Předchozí stránka';
    intl.firstPageLabel = 'První stránka';
    intl.lastPageLabel = 'Poslední stránka';
    intl.getRangeLabel = (page, pageSize, length) => {
        if (length === 0) return '0 z 0';
        const start = page * pageSize + 1;
        const end = Math.min((page + 1) * pageSize, length);
        return `${start} – ${end} z ${length}`;
    };
    return intl;
}

// Load Clarity before bootstrapping the app
loadClarity();

bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection(),
        provideRouter(
            appRoutes,
            withPreloading(NoPreloading),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
        ),
        provideAnimationsAsync(),
        provideHttpClient(withInterceptorsFromDi()),
        provideBrnCalendarI18n(CS_CALENDAR_I18N),
        provideNativeDateAdapter(),
        // TODO: Add provideSpartanHlm() when addressing toasts hidden behind Angular 21+ overlays.
        // https://www.spartan.ng/components/sonner#angular-21-compatibility
        importProvidersFrom(
            // Fuse modules
            FuseModule,
            FuseConfigModule.forRoot(appConfig),
            //TODO: delete after full spartan integration
            FuseMockApiModule.forRoot(mockApiServices),
            FuseLoadingBarModule,
            // Core module (provides AuthModule, ErrorModule, LanguageModule, IconsModule, TranslocoCoreModule)
            CoreModule,
            // Layout module
            LayoutModule,
            // Third-party modules
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: (http: HttpClient) => new NgxTranslateCacheBusterLoader(http),
                    deps: [HttpClient],
                },
            }),
        ),
        // Sentry error handling
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
        // Date adapter (used by lazy-loaded form components with MatDatepicker)
        provideMomentDateAdapter({
            parse: { dateInput: moment.ISO_8601 },
            display: { dateInput: 'LL', monthYearLabel: 'MMM YYYY', dateA11yLabel: 'LL', monthYearA11yLabel: 'MMMM YYYY' },
        }),
        { provide: MAT_DATE_LOCALE, useValue: 'cs-CZ' },
        // Czech paginator labels
        { provide: MatPaginatorIntl, useFactory: czechPaginatorIntl },
        // API base paths
        {
            provide: API_BASE_PATH,
            useValue: environment.apiBaseUrl,
        },
        {
            provide: API_USERS_BASE_PATH,
            useValue: environment.apiUsersBaseUrl,
        },
        {
            provide: INTERNAL_API_BASE_PATH,
            useValue: environment.apiInternalBaseUrl,
        },
    ],
}).catch(err => console.error(err));
